import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTenant } from '../../hooks/useTenant';
import { useAuthStore } from '../../stores/authStore';
import { Loading } from '../../components/common/Loading';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { handleGoogleCallback, handleFacebookCallback } from '../../api/auth';
import { ROUTES } from '../../utils/constants';

export function AuthCallback() {
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser } = useAuthStore();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const errorParam = searchParams.get('error');

      if (errorParam) {
        setError(searchParams.get('error_description') || 'Authentication was cancelled');
        return;
      }

      if (!code || !state) {
        setError('Invalid callback parameters');
        return;
      }

      try {
        // Check for invite token from session storage
        const inviteToken = sessionStorage.getItem('invite_token');
        if (inviteToken) {
          sessionStorage.removeItem('invite_token');
        }

        // Determine provider from state or URL
        // For simplicity, we'll try Google first, then Facebook
        let response;
        try {
          response = await handleGoogleCallback(tenant, code, state, inviteToken || undefined);
        } catch {
          response = await handleFacebookCallback(tenant, code, state);
        }

        setUser(response.user);

        // Store token in localStorage for API requests
        if (response.token) {
          localStorage.setItem('auth_token', response.token);
        }

        // Check if user needs onboarding
        // For now, just redirect to home
        navigate(ROUTES.home(tenant), { replace: true });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Authentication failed');
      }
    };

    if (tenant) {
      handleCallback();
    }
  }, [tenant, searchParams, setUser, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="max-w-md w-full text-center">
          <div className="mb-4">
            <div className="mx-auto h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
              <svg
                className="h-6 w-6 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            Authentication Failed
          </h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={() => navigate(ROUTES.login(tenant))}>
            Try Again
          </Button>
        </Card>
      </div>
    );
  }

  return <Loading fullScreen message="Completing sign in..." />;
}
